import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { Organisation } from '../types';

interface OrganisationPickerProps {
  sourceId: string | null;
  targetId: string | null;
  onSelectSource: (id: string, name: string) => void;
  onSelectTarget: (id: string, name: string) => void;
  disabled?: boolean;
}

interface OrganisationsResponse {
  organisations: Organisation[];
  pageNumber: number;
  pageLength: number;
}

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

function useOrganisations() {
  return useQuery({
    queryKey: ['account-migration', 'organisations'],
    queryFn: async () => {
      const allOrgs: Organisation[] = [];
      let pageNumber = 0;
      let hasMore = true;

      while (hasMore && pageNumber < MAX_PAGES) {
        const data = await api.get<OrganisationsResponse>('/v4/legacy/organisations', {
          pageLength: PAGE_SIZE,
          pageNumber,
          sortKey: 'name',
          sortDirection: 1,
          tags: 'customer',
        });
        allOrgs.push(...data.organisations);
        hasMore = data.organisations.length === PAGE_SIZE;
        pageNumber++;
      }

      return allOrgs;
    },
    staleTime: 5 * 60 * 1000,
  });
}

interface OrgComboboxProps {
  label: string;
  value: string | null;
  excludeId: string | null;
  organisations: Organisation[];
  onSelect: (id: string, name: string) => void;
  disabled?: boolean;
}

function OrgCombobox({ label, value, excludeId, organisations, onSelect, disabled }: OrgComboboxProps) {
  const [open, setOpen] = useState(false);
  const selectedOrg = organisations.find((o) => o._id === value);
  const filteredOrgs = organisations.filter((o) => o._id !== excludeId);

  return (
    <div className="flex-1 space-y-2">
      <label className="text-sm font-medium">
        {label} <span className="text-destructive">*</span>
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            {selectedOrg
              ? `${selectedOrg.name}${selectedOrg.org_num ? ` (${selectedOrg.org_num})` : ''}`
              : `Select ${label.toLowerCase()}...`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search organisations..." />
            <CommandList>
              <CommandEmpty>No organisations found.</CommandEmpty>
              <CommandGroup>
                {filteredOrgs.map((org) => (
                  <CommandItem
                    key={org._id}
                    value={`${org.name} ${org.org_num || ''}`}
                    onSelect={() => {
                      onSelect(org._id, org.name);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === org._id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {org.name}{org.org_num ? ` (${org.org_num})` : ''}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function OrganisationPicker({
  sourceId,
  targetId,
  onSelectSource,
  onSelectTarget,
  disabled = false,
}: OrganisationPickerProps) {
  const { data: organisations, isLoading, error } = useOrganisations();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select Accounts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select Accounts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive">
            Failed to load organisations. Please try again.
          </div>
        </CardContent>
      </Card>
    );
  }

  const sameAccountSelected = sourceId && targetId && sourceId === targetId;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Accounts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <OrgCombobox
            label="Source Account"
            value={sourceId}
            excludeId={targetId}
            organisations={organisations || []}
            onSelect={onSelectSource}
            disabled={disabled}
          />
          <OrgCombobox
            label="Target Account"
            value={targetId}
            excludeId={sourceId}
            organisations={organisations || []}
            onSelect={onSelectTarget}
            disabled={disabled}
          />
        </div>
        {sameAccountSelected && (
          <p className="text-sm text-destructive">
            Source and target accounts must be different.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
